import React from 'react';
import { format, addDays } from 'date-fns';

export default function ProposalClientSection({ proposal }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  // Calculate valid_until: use stored value or calculate created_at + 30 days
  const getValidUntil = () => {
    if (proposal.valid_until) {
      return formatDate(proposal.valid_until);
    }
    // Calculate 30 days from created_at
    if (proposal.created_at) {
      try {
        const createdDate = new Date(proposal.created_at);
        const validDate = addDays(createdDate, 30);
        return format(validDate, 'MMM d, yyyy');
      } catch {
        return '30 Days from Creation';
      }
    }
    return '30 Days from Creation';
  };

  // Build address strings
  const clientCity = [proposal.client_city, proposal.client_state, proposal.client_zip].filter(Boolean).join(', ');
  const serviceAddress = proposal.service_address_street || '';
  const serviceCity = [proposal.service_address_city, proposal.service_address_state, proposal.service_address_zip].filter(Boolean).join(', ');
  const hasServiceAddress = serviceAddress || serviceCity;

  // Get contact name
  const contactName = proposal.contact_name || 
    [proposal.contact_first_name, proposal.contact_last_name].filter(Boolean).join(' ');

  // Get created by name - prefer created_by_name, fallback to created_by only if it's a real name (not UUID)
  const getCreatedByName = () => {
    if (proposal.created_by_name) return proposal.created_by_name;
    // Check if created_by looks like a UUID (contains dashes and is 36 chars) - if so, don't show it
    if (proposal.created_by && !proposal.created_by.includes('-')) {
      return proposal.created_by;
    }
    return '';
  };
  const createdByName = getCreatedByName();

  return (
    <div className="mb-4">
      {/* Info Grid - 3 columns (removed title row per request) */}
      <div className="grid grid-cols-3 gap-4 text-sm">
        {/* Client */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Bill To</p>
          <p className="font-semibold text-slate-900">{proposal.client_name}</p>
          {contactName && <p className="text-slate-600 text-xs">{contactName}</p>}
          {proposal.client_street && <p className="text-slate-600 text-xs">{proposal.client_street}</p>}
          {clientCity && <p className="text-slate-600 text-xs">{clientCity}</p>}
          {proposal.client_phone && <p className="text-slate-600 text-xs">{proposal.client_phone}</p>}
          {proposal.client_email && <p className="text-slate-600 text-xs break-words">{proposal.client_email}</p>}
          {proposal.ar_email_address && (
            <p className="text-slate-600 text-xs mt-1 break-words">
              <span className="text-slate-400">AP Email:</span> {proposal.ar_email_address}
            </p>
          )}
        </div>
        
        {/* Service Address */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Service Location</p>
          {hasServiceAddress ? (
            <>
              {serviceAddress && <p className="font-semibold text-slate-900">{serviceAddress}</p>}
              {serviceCity && <p className="text-slate-600 text-xs">{serviceCity}</p>}
            </>
          ) : (
            <p className="text-slate-400 text-xs italic">Same as billing</p>
          )}
          {proposal.job_title && (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Job Title</p>
              <p className="font-semibold text-slate-900">{proposal.job_title}</p>
            </div>
          )}
        </div>
        
        {/* Details */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Details</p>
          <div className="space-y-0.5 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-500">Date:</span>
              <span className="font-medium text-slate-700">{formatDate(proposal.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Valid Until:</span>
              <span className="font-medium text-slate-700">{getValidUntil()}</span>
            </div>
            {createdByName && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-[10px] text-slate-400">Created by</p>
                <p className="font-medium text-slate-700">{createdByName}</p>
                <p className="text-[10px] text-slate-400">{formatDateTime(proposal.created_at)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
